/**
 * v4.11.1 — Fleet Drawer KPI Panel.
 *
 * Three premium donut visualizations:
 *   1. Taps · last 24h          (this card · location total)   — fuchsia + orange
 *   2. Taps · lifetime          (this card · location total)   — fuchsia + orange
 *   3. Review Shield · rating   (this card vs location avg)    — green + orange
 *
 * Compact, tablet-friendly, premium Operational Base register.
 * No numbered markers — donuts speak for themselves.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Star } from "lucide-react";
import api from "../../lib/api";

const COLORS = {
    card: "#ec4899",        // fuchsia
    location: "#f59e0b",    // orange
    positive: "#10b981",    // green
};

// Conic donut. `primaryPct` paints the leading slice; `accentColor` the
// remainder. Center text sits in a tight punched-out hole. Inline SVG-free
// for max responsiveness + perfect alignment with the panel's typography.
const Donut = ({ primaryPct, primaryColor, accentColor, center, sub, size = 88, testId }) => {
    const pct = Math.max(0, Math.min(100, primaryPct || 0));
    return (
        <div
            data-testid={testId}
            className="relative flex-shrink-0"
            style={{
                width: size,
                height: size,
                borderRadius: "9999px",
                background: `conic-gradient(${primaryColor} 0% ${pct}%, ${accentColor} ${pct}% 100%)`,
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
            }}
        >
            <div
                className="absolute inset-2 rounded-full flex items-center justify-center text-center"
                style={{ background: "rgb(15, 23, 42)" }}
            >
                <div>
                    <p className="text-base font-semibold text-slate-100 leading-none">
                        {center}
                    </p>
                    {sub ? (
                        <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider">
                            {sub}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const LegendRow = ({ color, label, value, valueSub, icon }) => (
    <li className="flex items-center justify-between text-xs">
        <span className="inline-flex items-center gap-1.5 text-slate-300 truncate">
            <span
                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: color }}
            />
            {label}
        </span>
        <span className="font-mono text-slate-200 flex items-center gap-1">
            {value}
            {icon}
            {valueSub ? (
                <span className="text-slate-500 ml-1 text-[11px] font-sans">{valueSub}</span>
            ) : null}
        </span>
    </li>
);

const PanelTitle = ({ children }) => {
    const { t } = useTranslation();
    return (
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400 mb-2">
            <span>{children}</span>
            <Info size={11} className="text-slate-500" strokeWidth={1.75} aria-hidden />
            <span className="sr-only">{t("common.info", { defaultValue: "info" })}</span>
        </div>
    );
};

export default function CardKpiPanel({ cardId }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!cardId) return;
        setLoading(true);
        api.get(`/card-fleet/card/${cardId}/kpis`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [cardId]);

    if (!cardId) return null;
    if (loading || !data) {
        return (
            <div data-testid="card-kpi-panel-loading" className="text-xs text-slate-500">
                {t("common.loading")}
            </div>
        );
    }

    const t24 = data.taps_24h || {};
    const tlt = data.taps_lifetime || {};
    const rsr = data.review_shield_rating || {};

    // Card pct of total — render the THIS-CARD slice in fuchsia, fill in orange.
    const card24Pct = t24.location_total
        ? Math.round((t24.card / t24.location_total) * 100)
        : (t24.card ? 100 : 0);
    const cardLifetimePct = tlt.location_total
        ? Math.round((tlt.card / tlt.location_total) * 100)
        : (tlt.card ? 100 : 0);
    const cardRating = typeof rsr.card === "number" ? rsr.card : null;
    const locationRating = typeof rsr.location_avg === "number" ? rsr.location_avg : null;
    const ratingPct = cardRating !== null ? Math.round((cardRating / 5) * 100) : 0;

    return (
        <div className="space-y-5" data-testid="card-kpi-panel">
            {/* Taps · last 24h */}
            <div data-testid="kpi-block-taps-24h">
                <PanelTitle>{t("card_fleet.kpi_panel.taps_24h_title")}</PanelTitle>
                <div className="flex items-center gap-4">
                    <Donut
                        primaryPct={card24Pct}
                        primaryColor={COLORS.card}
                        accentColor={COLORS.location}
                        center={(t24.location_total || t24.card || 0).toLocaleString()}
                        sub={t("card_fleet.kpi_panel.total")}
                        testId="kpi-donut-taps-24h"
                    />
                    <ul className="flex-1 space-y-1.5">
                        <LegendRow
                            color={COLORS.card}
                            label={t("card_fleet.kpi_panel.this_card")}
                            value={(t24.card || 0).toLocaleString()}
                            valueSub={`(${card24Pct}%)`}
                        />
                        <LegendRow
                            color={COLORS.location}
                            label={t("card_fleet.kpi_panel.location_total")}
                            value={Math.max(0, (t24.location_total || 0) - (t24.card || 0)).toLocaleString()}
                            valueSub={`(${100 - card24Pct}%)`}
                        />
                    </ul>
                </div>
            </div>

            {/* Taps · lifetime */}
            <div data-testid="kpi-block-taps-lifetime">
                <PanelTitle>{t("card_fleet.kpi_panel.taps_lifetime_title")}</PanelTitle>
                <div className="flex items-center gap-4">
                    <Donut
                        primaryPct={cardLifetimePct}
                        primaryColor={COLORS.card}
                        accentColor={COLORS.location}
                        center={(tlt.location_total || tlt.card || 0).toLocaleString()}
                        sub={t("card_fleet.kpi_panel.total")}
                        testId="kpi-donut-taps-lifetime"
                    />
                    <ul className="flex-1 space-y-1.5">
                        <LegendRow
                            color={COLORS.card}
                            label={t("card_fleet.kpi_panel.this_card")}
                            value={(tlt.card || 0).toLocaleString()}
                            valueSub={`(${cardLifetimePct}%)`}
                        />
                        <LegendRow
                            color={COLORS.location}
                            label={t("card_fleet.kpi_panel.location_total")}
                            value={Math.max(0, (tlt.location_total || 0) - (tlt.card || 0)).toLocaleString()}
                            valueSub={`(${100 - cardLifetimePct}%)`}
                        />
                    </ul>
                </div>
            </div>

            {/* Review Shield rating */}
            <div data-testid="kpi-block-review-rating">
                <PanelTitle>{t("card_fleet.kpi_panel.rs_rating_title")}</PanelTitle>
                {cardRating === null && locationRating === null ? (
                    <p className="text-xs text-slate-500">
                        {t("card_fleet.kpi_panel.no_reviews_yet")}
                    </p>
                ) : (
                    <div className="flex items-center gap-4">
                        <Donut
                            primaryPct={ratingPct}
                            primaryColor={COLORS.positive}
                            accentColor={COLORS.location}
                            center={cardRating !== null ? `${cardRating.toFixed(1)} ★` : "—"}
                            sub={t("card_fleet.kpi_panel.average")}
                            testId="kpi-donut-review-rating"
                        />
                        <ul className="flex-1 space-y-1.5">
                            <LegendRow
                                color={COLORS.positive}
                                label={t("card_fleet.kpi_panel.this_card")}
                                value={cardRating !== null ? cardRating.toFixed(1) : "—"}
                                icon={<Star size={11} className="text-amber-400 fill-amber-400" strokeWidth={1.5} />}
                            />
                            <LegendRow
                                color={COLORS.location}
                                label={t("card_fleet.kpi_panel.location_avg")}
                                value={locationRating !== null ? locationRating.toFixed(1) : "—"}
                                icon={<Star size={11} className="text-amber-400 fill-amber-400" strokeWidth={1.5} />}
                            />
                        </ul>
                    </div>
                )}
                <p className="text-[10px] text-slate-500 mt-2">
                    {t("card_fleet.kpi_panel.rs_footnote")}
                </p>
            </div>
        </div>
    );
}
